import React from 'react';

/**
 * Catches render-time errors so one broken page cannot take down the whole app.
 *
 * React unmounts the entire tree when a render throws and nothing catches it —
 * which is why a single unguarded property read ("Cannot read properties of
 * undefined") turned into a blank screen with a stack trace rather than a page
 * with a message on it.
 *
 * This is the safety net, not the fix: pages should still handle their own
 * failures, because a page that knows what failed can offer a retry, whereas
 * this can only apologise. It exists so the failure mode is a readable message
 * instead of a white screen.
 */
class ErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { error: null };
	}

	static getDerivedStateFromError(error) {
		return { error };
	}

	componentDidCatch(error, info) {
		console.error('Unhandled render error:', error, info?.componentStack);
	}

	handleReload = () => {
		this.setState({ error: null });
		window.location.reload();
	};

	handleGoHome = () => {
		this.setState({ error: null });
		window.location.href = '/';
	};

	render() {
		const { error } = this.state;
		if (!error) return this.props.children;

		return (
			<div
				className="d-flex flex-column align-items-center justify-content-center text-center p-5"
				style={{ minHeight: '60vh' }}
			>
				<h4
					className="mb-2"
					style={{ fontWeight: 700 }}
				>
					Something went wrong on this page
				</h4>
				<p
					className="mb-4"
					style={{ color: '#64748B', maxWidth: '520px' }}
				>
					The page could not be displayed. Your data is safe — reloading usually fixes it. If it
					keeps happening, please let us know what you were doing at the time.
				</p>

				<div className="d-flex gap-3">
					<button
						type="button"
						className="btn btn-primary"
						onClick={this.handleReload}
					>
						Reload page
					</button>
					<button
						type="button"
						className="btn btn-outline-secondary"
						onClick={this.handleGoHome}
					>
						Go to dashboard
					</button>
				</div>

				{/* Details in development only — useful to a developer, noise to a user. */}
				{process.env.NODE_ENV === 'development' && (
					<details
						className="mt-4 text-start"
						style={{ maxWidth: '720px' }}
					>
						<summary style={{ cursor: 'pointer', color: '#64748B' }}>
							Error details (development only)
						</summary>
						<pre
							className="mt-2 p-3 rounded"
							style={{
								backgroundColor: '#F8FAFC',
								fontSize: '12px',
								whiteSpace: 'pre-wrap',
								overflowX: 'auto',
							}}
						>
							{error?.stack ?? String(error)}
						</pre>
					</details>
				)}
			</div>
		);
	}
}

export default ErrorBoundary;
